import { useCallback, useEffect, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { bypassCheckoutBackGuardOnce } from 'features/checkout/hooks/checkout-navigation-bypass';

import { useOrderStatus } from 'hooks/order-status';
import { useCart } from 'hooks/use-cart';
import { selectors, useAppSelector } from 'state/global-state';
import { isWeb } from 'utils/environment';
import { logger } from 'utils/logger';
import {
  clearPendingSubmittedOrder,
  getPendingSubmittedOrder,
} from 'utils/order/pending-submitted-order';
import { routes } from 'utils/routing';

interface UseRecoverPendingSubmittedOrderOptions {
  checkoutRoute?: string | null;
  enabled?: boolean;
}

export const useRecoverPendingSubmittedOrder = ({
  checkoutRoute,
  enabled = true,
}: UseRecoverPendingSubmittedOrderOptions = {}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { emptyCart } = useCart();
  const { emptyMinimalCart } = useMinimalCart();
  const enableMinimalCartUi = useMinimalCartUi();
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const pendingSubmittedOrder = enabled ? getPendingSubmittedOrder() : null;
  const activePendingOrderId =
    pendingSubmittedOrder && (!pricedOrderId || pendingSubmittedOrder.rbiOrderId === pricedOrderId)
      ? pendingSubmittedOrder.rbiOrderId
      : null;

  const [pendingOrderId, setPendingOrderId] = useState<string | null>(activePendingOrderId);

  useEffect(() => {
    if (activePendingOrderId !== pendingOrderId) {
      setPendingOrderId(activePendingOrderId);
    }
  }, [activePendingOrderId, pendingOrderId]);

  useEffect(() => {
    if (
      pendingSubmittedOrder?.rbiOrderId &&
      pricedOrderId &&
      pendingSubmittedOrder.rbiOrderId !== pricedOrderId
    ) {
      clearPendingSubmittedOrder();
    }
  }, [pendingSubmittedOrder?.rbiOrderId, pricedOrderId]);

  const handleSuccess = useCallback(() => {
    if (!pendingOrderId) {
      return;
    }

    const orderConfirmationUrl = `${routes.orderConfirmation}/${pendingOrderId}?showToast=true`;
    clearPendingSubmittedOrder();
    setPendingOrderId(null);
    bypassCheckoutBackGuardOnce();

    if (enableMinimalCartUi) {
      emptyMinimalCart();
    } else {
      emptyCart();
    }

    router.replace(orderConfirmationUrl);
  }, [emptyCart, emptyMinimalCart, enableMinimalCartUi, pendingOrderId, router]);

  const { failure, serverOrder } = useOrderStatus({
    onSuccess: handleSuccess,
    rbiOrderId: pendingOrderId,
    skip: !enabled || !pendingOrderId,
  });

  useEffect(() => {
    if (!pendingOrderId || !checkoutRoute) {
      return;
    }

    const isOnCheckoutRoute =
      pathname === checkoutRoute ||
      pathname.startsWith(routes.cartPayment) ||
      pathname.startsWith(routes.cartCheckout);

    if (isOnCheckoutRoute || pathname.startsWith(routes.orderConfirmation)) {
      return;
    }

    router.replace(checkoutRoute);
  }, [checkoutRoute, pendingOrderId, pathname, router]);

  useEffect(() => {
    if (!pendingOrderId || !failure) {
      return;
    }

    logger.warn({
      message: '[useRecoverPendingSubmittedOrder] Clearing pending submitted order after failure',
      rbiOrderId: pendingOrderId,
      orderStatus: serverOrder?.status,
      orderDeliveryStatus: serverOrder?.delivery?.status,
    });

    clearPendingSubmittedOrder();
    setPendingOrderId(null);
    if (pathname.startsWith(routes.cartPayment) || pathname.startsWith(routes.cartCheckout)) {
      router.replace(routes.cart);
    }
  }, [failure, pendingOrderId, pathname, router, serverOrder]);

  useEffect(() => {
    if (!pendingOrderId || !isWeb) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pendingOrderId]);
};
